import * as R from 'ramda';
import React from 'react';
import styled, { css } from 'styled-components';
import { width, space, fontSize, maxWidth } from 'styled-system';
// components
import TextComponent from '../text';
// features
import { AuthByCurrentBranch } from '../../features/permission';
import { convertDataTypeToUiTableField } from '../../features/configurations/helpers';
// helpers
import * as G from '../../helpers';
// constants
import * as GC from '../../constants';
// forms
import { Checkbox } from '../../forms';
//////////////////////////////////////////////////

const renderMultiItems = (
  entity,
  field,
  index,
) => (
  <TableColumn key={index} width={field.width}>
    <TextComponent
      maxWidth='95%'
      display='block'
      withEllipsis={true}
      title={R.or(entity[field.nameForAttribute], []).join(', ')}
    >
      {R.or(entity[field.nameForAttribute], []).join(', ')}
    </TextComponent>
  </TableColumn>
);

export const TableContainer = styled.div`
  ${space}
  ${width}
  ${maxWidth}
  ${fontSize}

  border: 1px solid;
  position: relative;
  z-index: ${({ zi }) => zi || 'auto'};
  min-width: ${({ minWidth }) => minWidth};
  overflow: ${({ overflow }) => overflow || 'scroll'};
  max-height: ${({ maxHeight }) => maxHeight || 'calc(100vh - 145px)'};
  border-color: ${({ borderColor }) => borderColor || G.getTheme('tables.rows.borderColor')};
  & * {
    z-index: auto;
  }
`;

// NOTE: border should be before
export const TableRow = styled.div`
  ${space}
  ${width}
  ${fontSize}

  border: ${({ isHeader }) => R.or(R.and(isHeader, 'none'), '1px solid')};
  border-top: none;
  border-left: none;
  flex-wrap: no-wrap;
  align-items: center;
  flex-direction: row;
  min-width: max-content;
  top: ${({ top }) => top};
  text-align: ${({ ta }) => ta};
  z-index: ${({ zIndex }) => zIndex};
  position: ${({ position }) => position};
  height: ${({ height }) => height || 'auto'};
  justify-content: ${({ jc }) => jc || 'center'};
  display: ${({ display }) => display || 'block'};
  min-height: ${({ minHeight }) => minHeight || '56px'};
  color: ${({ textColor }) => textColor || G.getTheme('tables.rows.textColor')};
  background-color: ${({ bgColor }) => bgColor || G.getTheme('tables.rows.bgColor')};
  border-color: ${({ borderColor }) => borderColor || G.getTheme('tables.rows.borderColor')};
`;

export const TableWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

export const FixedContentWrapper = styled.div`
  top: 61px;
  left: 56px;
  position: fixed;
  width: calc(100vw - 70px);
  transition: all .2s linear;
`;

export const TableColumn = styled.div`
  ${space}
  ${width}

  flex-grow: 0;
  flex-shrink: 0;
  vertical-align: top;
  display: inline-block;

  text-align: ${({ ta }) => ta};
  word-break: ${({ brakeWord }) => R.and(G.isTrue(brakeWord), 'break-word')};

  ${({ specificStyles }) => specificStyles};
`;

TableColumn.defaultProps = {
  pr: 10,
};

// NOTE: border should be before
export const FixedColumnRow = styled.div`
  ${space}
  ${width}

  top: 0;
  left: 0;
  display: flex;
  border: ${({ isHeader }) => G.ifElse(isHeader, '2px solid', '1px solid')};
  border-top: none;
  border-left: none;
  align-items: center;
  height: ${({ height }) => height || '40px'};
  position:  ${({ fixed }) => G.ifElse(fixed, 'absolute', 'initial')};
  z-index:  ${({ fixed }) => G.ifElse(fixed, 10, 'initial')} !important;
  color: ${({ textColor }) => textColor || G.getTheme('tables.rows.textColor')};
  background-color: ${({ bgColor }) => bgColor || G.getTheme('tables.header.greyBgColor')};
  border-color: ${({ borderColor }) => borderColor || G.getTheme('tables.rows.borderColor')};
`;

export const TableScrollWrapper = styled.div`
  display: flex;
  min-width: 100%;
  width: max-content;
  flex-direction: column;
`;

export const FixedRow = styled.div`
  ${space}

  top: 0;
  left: 0;
  z-index: 9;
  display: flex;
  position: absolute;
  border-right: 1px solid;
  border-bottom: 1px solid;
  width: calc(100vw - 75px);
  transition: width .2s linear;

  height: ${({ height }) => height || '40px'};
  color: ${({ textColor }) => textColor || G.getTheme('tables.rows.textColor')};
  background-color: ${({ bgColor }) => bgColor || G.getTheme('tables.header.greyBgColor')};
  border-color: ${({ borderColor }) => borderColor || G.getTheme('tables.rows.borderColor')};
`;

export const FixedColumn = styled.div`
  top: 0;
  display: flex;
  position: absolute;
  width: max-content;
  height: max-content;
  flex-direction: column;
  max-height: calc(100vh - 155px);
  z-index: ${({ zIndex }) => zIndex};
  background-color: ${({ bgColor }) => bgColor || G.getTheme('tables.rows.bgColor')};
  ${({ horizontalPosition }) => horizontalPosition}: ${({ distance }) => distance};
  box-shadow: ${({ horizontalPosition }) => (
    R.and(
      R.equals(horizontalPosition, 'left'),
      '2px 0px 1px 1px rgba(221, 221, 221, 0.5)',
    )
  )};
`;

const fixedRow = css`
  width: 100%;
  height: 100%;
  display: flex;
  overflow: auto;
  padding: 20px 15px;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const fixedColumn = css`
  overflow: auto;
  padding-top: 56px;
  flex-direction: column;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const classStyles = {
  'fixedRow': fixedRow,
  'fixedColumn': fixedColumn,
};

export const EmptyList = styled.div`
  ${width}

  font-size: 36px;
  padding: 50px 0;
  text-align: center;
  color: ${() => G.getTheme('colors.lightGrey')};
`;

const getFieldText = (data) => {
  if (G.isArray(data)) {
    return R.compose(
      R.join(': '),
      R.map((dataItem) => G.getWindowLocale(dataItem, dataItem)),
    )(data);
  }
  return G.getWindowLocale(data, data);
};

export const renderFixedRow = (
  { handleRegisterChild },
  { rowHeight, paddingLeft, fixedRowFields },
) => (
  <FixedRow pl={paddingLeft} height={rowHeight}>
    <div css={classStyles.fixedRow} ref={(ref) => handleRegisterChild(ref, 'fixedRow')}>
      {
        G.getNewArrayFromObjectWithHandling(
        (field, key) => {
          const text = getFieldText(field.name);
          return (
            <TableColumn key={key} width={field.width}>
              <TextComponent
                width='100%'
                title={text}
                display='block'
                withEllipsis={true}
              >
                {text}
              </TextComponent>
            </TableColumn>
          );
        }, fixedRowFields)
      }
    </div>
  </FixedRow>
);

export const renderFixedColumnRow = (
  { rowHeight, fixedColumnOptions },
  customRenderTableColumn,
) => (
  <FixedColumnRow
    ta='left'
    py='18px'
    px='15px'
    fixed={true}
    height={rowHeight}
  >
    {R.and(G.isNotNil(customRenderTableColumn), customRenderTableColumn())}
    {
      fixedColumnOptions.map((option, index) => (
        <TableColumn key={index} width={option.width}>
          {G.getWindowLocale(option.name, option.nameForAttribute)}
        </TableColumn>))
    }
  </FixedColumnRow>
);

export const renderTableColumn = (
  entity,
  field,
  index,
  customRenderTable,
  customColumn,
) => {
  if (R.equals(field.customLogic, true)) {
    return customRenderTable(entity, field, index);
  }
  if (R.is(Function, field.customLogic)) {
    return field.customLogic(entity[field.nameForAttribute], entity, field, index);
  }
  if (field.label) {
    return (
      <TableColumn key={index} width={field.width}>
        {customColumn[field.label](entity, field.nameForAttribute)}
      </TableColumn>
    );
  }
  if (G.isTrue(field.isRedirectLink)) {
    return (
      <TableColumn key={index} width={field.width}>
        <a href={G.makeURLString(entity.url)} target='_blank'>{entity.url}</a>
      </TableColumn>
    );
  }
  if (R.is(Boolean, entity[field.nameForAttribute])) {
    return (
      <TableColumn key={index} width={field.width} specificStyles={field.specificStyles}>
        {convertDataTypeToUiTableField(entity[field.nameForAttribute])}
      </TableColumn>
    );
  }
  if (G.isNotNil(entity[field.nameForAttribute])) {
    return (
      <TableColumn key={index} brakeWord={true} width={field.width} specificStyles={field.specificStyles}>
        <TextComponent
          display='block'
          withEllipsis={true}
          width={R.subtract(field.width, 15)}
          title={entity[field.nameForAttribute]}
        >
          {entity[field.nameForAttribute]}
        </TextComponent>
      </TableColumn>
    );
  }
  if (G.isNotNilAndNotEmpty(field.inputs)) {
    return (
      <TableColumn key={index} width={field.width} specificStyles={field.specificStyles}>
        {field.inputs.map((input) => entity[input.nameForAttribute]).join(' ')}
      </TableColumn>
    );
  }
  return <TableColumn key={index} width={field.width} />;
};

export const renderCheckBox = (
  entity,
  selectedList,
  withPermit,
  handleSelect,
) => (
  G.ifElse(
    G.isTrue(withPermit),
    <AuthByCurrentBranch elementBranchGuid={entity[GC.FIELD_BRANCH_GUID]}>
      <Checkbox
        type='checkbox'
        name={entity.guid}
        onClick={handleSelect}
        checked={R.includes(entity.guid, selectedList)} />
    </AuthByCurrentBranch>,
    <Checkbox
      type='checkbox'
      name={entity.guid}
      onClick={handleSelect}
      checked={R.includes(entity.guid, selectedList)} />,
  )
);

export const renderFixedRows = (
  entities,
  customRenderTable,
  {
    withPermit,
    handleSelect,
    selectedList,
    fixedColumnOptions,
  },
  columnsArr,
) => {
  if (R.and(R.isEmpty(columnsArr), R.isEmpty(fixedColumnOptions))) return null;
  return (
    entities.map((entity) => (
      <TableRow
        py='18px'
        ta='left'
        px='15px'
        height={56}
        display='flex'
        key={entity.guid}
      >
        <TableColumn width='40px'>
          {renderCheckBox(entity, selectedList, withPermit, handleSelect)}
        </TableColumn>
        {
          fixedColumnOptions.map((option, index) => (
            renderTableColumn(entity, option, index, customRenderTable)
          ))
        }
      </TableRow>
    ))
  );
};

export const renderFixedRowsRight = (
  entities,
  {
    withPermit,
    elementActionsComponent,
  },
) => (
  entities.map((entity) => (
    <TableRow
      py='10px'
      ta='left'
      jc='center'
      height={56}
      width='50px'
      display='flex'
      key={entity.guid}
    >
      <TableColumn width='15px'>
        {
          G.ifElse(
            G.isTrue(withPermit),
            <AuthByCurrentBranch elementBranchGuid={entity[GC.FIELD_BRANCH_GUID]}>
              {elementActionsComponent(entity)}
            </AuthByCurrentBranch>,
            elementActionsComponent(entity),
          )
        }
      </TableColumn>
    </TableRow>
  ))
);

export const renderRows = (
  entities = [],
  {
    columnsArr,
    customColumn,
    customRenderTable,
  },
) => (
  entities.map((entity) => (
    <TableRow
      pl='15px'
      py='10px'
      ta='left'
      height={56}
      display='flex'
      jc='flex-start'
      key={entity.guid}
    >
      {
        G.getNewArrayFromObjectWithHandling(
          (field, key) => renderTableColumn(
            entity,
            field,
            `${entity.guid}${key}`,
            customRenderTable,
            customColumn,
          ),
          columnsArr,
        )
      }
      <TableColumn width='50px' />
    </TableRow>
  ))
);

export const renderListItems = (
  entities = [],
  columnsArr = [],
  fixedColumnOptions = [],
  customColumn,
) => {
  if (R.isEmpty(entities)) {
    const txt = G.getWindowLocale('titles:empty-list', 'Empty List');
    return (
      <EmptyList>{txt}</EmptyList>
    );
  } else if (R.and(R.isEmpty(columnsArr), R.isEmpty(fixedColumnOptions))) {
    const txt = G.getWindowLocale(
      'messages:add-field-to-current-report',
      'Please, Select Report or Add Fields to Current Report',
    );
    return (
      <EmptyList>{txt}</EmptyList>
    );
  }
  return renderRows(entities, { columnsArr, customRenderTable: renderMultiItems, customColumn });
};

export const renderRightFixed = (
  handleRegisterChild,
  elementActionsComponent,
  entities,
  columnsArr,
  fixedColumnOptions,
  withPermit = false,
) => {
  if (R.and(R.isEmpty(columnsArr), R.isEmpty(fixedColumnOptions))) return null;
  return (
    <FixedColumn zIndex='8' distance='5px' horizontalPosition='right'>
      <div css={classStyles.fixedColumn} ref={(c) => handleRegisterChild(c, 'fixedColumnRight')}>
        {entities && renderFixedRowsRight(entities, { elementActionsComponent, withPermit })}
      </div>
    </FixedColumn>
  );
};

const renderTableCheckbox = (customCheckbox, handleSelect) => {
  if (G.isNotNil(customCheckbox)) return customCheckbox();
  return (
    <Checkbox
      name='all'
      type='checkbox'
      onClick={handleSelect} />
  );
};

export const renderFixedColumn = (
  rowHeight,
  fixedColumnOptions,
  entities,
  renderOptions,
  handleSelect,
  handleRegisterChild,
  columnsArr,
  customCheckbox,
) => (
  <FixedColumn zIndex='9' distance='0' horizontalPosition='left'>
    <div css={classStyles.fixedColumn} ref={(c) => handleRegisterChild(c, 'fixedColumnLeft')}>
      <FixedColumnRow
        ta='left'
        px='15px'
        py='18px'
        fixed={true}
        height={rowHeight}
        bgColor={G.getTheme('tables.header.bgColor')}
        textColor={G.getTheme('tables.header.textColor')}
        borderColor={G.getTheme('tables.header.borderColor')}
      >
        <TableColumn width='40px'>{renderTableCheckbox(customCheckbox, handleSelect)}</TableColumn>
        {
          fixedColumnOptions.map((option, index) => {
            const text = getFieldText(option.name);
            return (
              <TableColumn key={index} width={option.width}>
                <TextComponent
                  width='100%'
                  title={text}
                  display='block'
                  withEllipsis={true}
                >
                  {text}
                </TextComponent>
              </TableColumn>
            );
          })
        }
      </FixedColumnRow>
      {R.and(entities, renderFixedRows(entities, renderMultiItems, renderOptions, columnsArr))}
    </div>
  </FixedColumn>
);

export const renderFixedListItems = (
  rowHeight,
  tableContainerPaddingLeft,
  columnsArr,
  handleRegisterChild,
) => (
  <FixedRow
    height={rowHeight}
    pl={tableContainerPaddingLeft}
    bgColor={G.getTheme('tables.header.bgColor')}
    textColor={G.getTheme('tables.header.textColor')}
    borderColor={G.getTheme('tables.header.borderColor')}
  >
    <div css={classStyles.fixedRow} ref={(c) => handleRegisterChild(c, 'fixedRow')}>
      {
        G.getNewArrayFromObjectWithHandling(
          (field, key) => {
            const text = getFieldText(field.name);
            return (
              <TableColumn key={key} width={field.width}>
                <TextComponent
                  width='100%'
                  title={text}
                  display='block'
                  withEllipsis={true}
                >
                  {text}
                </TextComponent>
              </TableColumn>
            );
          },
          columnsArr,
        )
      }
      <TableColumn width='50px' />
    </div>
  </FixedRow>
);
